import { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classes from "./GuidedTour.module.css";

const gap = 12;
const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

// All coordinates are viewport-relative, including mobile browser zoom/resize.
function viewportBounds() {
  const viewport = window.visualViewport;
  const left = viewport?.offsetLeft || 0;
  const top = viewport?.offsetTop || 0;
  return { left: left + gap, top: top + gap,
    right: left + (viewport?.width || window.innerWidth) - gap,
    bottom: top + (viewport?.height || window.innerHeight) - gap };
}

function panelPosition(target, panel, bounds) {
  if (!target) return null;
  const x = clamp(target.left + (target.width - panel.width) / 2, bounds.left, bounds.right - panel.width);
  const y = clamp(target.top + (target.height - panel.height) / 2, bounds.top, bounds.bottom - panel.height);
  if (target.bottom + gap + panel.height <= bounds.bottom && target.bottom >= bounds.top)
    return { left: x, top: target.bottom + gap };
  if (target.top - gap - panel.height >= bounds.top && target.top <= bounds.bottom)
    return { left: x, top: target.top - gap - panel.height };
  if (target.right + gap + panel.width <= bounds.right)
    return { left: target.right + gap, top: y };
  if (target.left - gap - panel.width >= bounds.left)
    return { left: target.left - gap - panel.width, top: y };
  return null;
}

function scrollTargetNearTop(target, top) {
  // Support both document scrolling and scrollable editor containers.
  for (let parent = target.parentElement; parent; parent = parent.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(parent).overflowY) && parent.scrollHeight > parent.clientHeight) {
      parent.scrollBy({ top: target.getBoundingClientRect().top - top, behavior: "instant" });
    }
  }
  window.scrollBy({ top: target.getBoundingClientRect().top - top, behavior: "instant" });
}

export default function GuidedTour({ steps, onClose }) {
  const [index, setIndex] = useState(0);
  const [position, setPosition] = useState(null);
  const dialogRef = useRef(null);
  const panelRef = useRef(null);
  const id = useId();
  const step = steps[index];

  useLayoutEffect(() => {
    const opener = document.activeElement;
    const dialog = dialogRef.current;
    // Native modal focus containment also makes the editor inert during explanations.
    dialog.showModal();
    return () => {
      dialog.close();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    let frame;
    const target = step.getTarget ? step.getTarget() : document.querySelector(`[data-tour-target="${step.target}"]`);
    const measure = (reveal = false) => {
      const bounds = viewportBounds();
      panel.style.maxWidth = `${bounds.right - bounds.left}px`;
      panel.style.maxHeight = `${bounds.bottom - bounds.top}px`;
      const size = panel.getBoundingClientRect();
      let rect = target?.isConnected && target.getClientRects().length ? target.getBoundingClientRect() : null;
      const safeTop = Math.max(bounds.top, 60); // Leave the site's fixed menu clear.
      if (reveal && rect) {
        if (rect.top < safeTop || rect.bottom > bounds.bottom) {
          target.scrollIntoView({ block: "center", inline: "nearest", behavior: "instant" });
          rect = target.getBoundingClientRect();
        }
        if (!panelPosition(rect, size, bounds)) {
          scrollTargetNearTop(target, safeTop);
          rect = target.getBoundingClientRect();
        }
      }
      const place = panelPosition(rect, size, bounds) || {
        left: clamp(rect?.left ?? bounds.left, bounds.left, bounds.right - size.width),
        top: bounds.bottom - size.height,
      };
      const highlight = rect && rect.bottom > safeTop && rect.top < bounds.bottom ? {
        left: Math.max(bounds.left, rect.left - 4), top: Math.max(safeTop, rect.top - 4),
        right: Math.min(bounds.right, rect.right + 4), bottom: Math.min(bounds.bottom, rect.bottom + 4),
      } : null;
      // A tall/wide diagram may not fit beside the panel in landscape. Spotlight
      // its visible upper portion instead of drawing a highlight behind the text.
      if (highlight && highlight.right > place.left && highlight.left < place.left + size.width &&
          highlight.bottom > place.top && highlight.top < place.top + size.height) {
        highlight.bottom = Math.max(highlight.top, place.top - gap);
      }
      const next = { ...place, highlight, missing: !rect, target: step.target };
      setPosition(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => measure());
    };
    const resize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => measure(true));
    };
    measure(true);
    panel.focus({ preventScroll: true });
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", resize);
    window.visualViewport?.addEventListener("resize", resize);
    window.visualViewport?.addEventListener("scroll", schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(panel);
    if (target) observer.observe(target);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", resize);
      window.visualViewport?.removeEventListener("resize", resize);
      window.visualViewport?.removeEventListener("scroll", schedule);
    };
  }, [step]);

  const highlight = position?.highlight;
  return createPortal(<dialog ref={dialogRef} className={classes.overlay}
    aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={`${id}-progress ${id}-text`}
    onCancel={event => { event.preventDefault(); onClose(); }}
    onKeyDown={event => {
      // Background editors can have document-level shortcuts even when inert.
      // Keep tour keystrokes out of those handlers, including Escape and Undo.
      event.stopPropagation();
      if ((event.ctrlKey || event.metaKey) && ["z", "y"].includes(event.key.toLowerCase())) event.preventDefault();
      if (event.key !== "Tab") return;
      const buttons = [...panelRef.current.querySelectorAll("button:not(:disabled)")];
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !buttons.includes(active))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (active === last || !buttons.includes(active))) {
        event.preventDefault(); first.focus();
      }
    }}>
    {highlight && <div className={classes.spotlight} data-tour-highlight={position.target} aria-hidden="true"
      style={{ left: highlight.left, top: highlight.top,
        width: Math.max(0, highlight.right - highlight.left), height: Math.max(0, highlight.bottom - highlight.top) }} />}
    <section ref={panelRef} className={classes.panel} tabIndex={-1}
      aria-labelledby={`${id}-title`} aria-describedby={`${id}-progress ${id}-text`}
      style={{ left: position?.left, top: position?.top, visibility: position ? "visible" : "hidden" }}>
      <p id={`${id}-progress`} className={classes.progress}>Step {index + 1} of {steps.length}</p>
      <h2 id={`${id}-title`}>{step.title}</h2>
      <p id={`${id}-text`}>{step.text}{position?.missing && <span className={classes.unavailable}>
        {step.unavailableText || "This part of the designer is not currently available. You can continue or exit the tour."}
      </span>}</p>
      <div className={classes.controls}>
        <button type="button" className={classes.exit} onClick={onClose}>Exit tour</button>
        <button type="button" disabled={index === 0} onClick={() => setIndex(index - 1)}>Back</button>
        <button type="button" className={classes.next}
          onClick={() => index === steps.length - 1 ? onClose() : setIndex(index + 1)}>
          {index === steps.length - 1 ? "Finish tour" : "Next"}
        </button>
      </div>
    </section>
  </dialog>, document.body);
}
